/**
 * Free Shipping Bar - Dynamic Updates
 * Handles real-time updates across announcement bar, product page, and cart
 */

(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    container: '[data-free-shipping-bar]',
    productContainer: '[data-free-shipping-product]',
    message: '[data-shipping-message]',
    progress: '[data-shipping-progress]',
    percentage: '[data-shipping-percentage]',
    confetti: '[data-shipping-confetti]',
    productMessage: '[data-product-shipping-message]',
    productProgress: '[data-product-progress]'
  };

  const CART_EVENTS = [
    'cart:updated',
    'cart-updated', 
    'drawer:cart:updated',
    'cart:refresh'
  ];

  let updateInProgress = false;
  let updateQueued = false;
  let lastCartData = null;

  /**
   * Format money with proper currency
   */
  function formatMoney(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return '$' + amount.replace(/\.00$/, '');
    }
  }

  /**
   * Fetch current cart data
   */
  async function fetchCart() {
    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart.js' || '/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const data = await response.json();
      lastCartData = data;
      return data;
    } catch (error) {
      console.error('Free Shipping Bar - Cart fetch error:', error);
      // Return last known data or empty cart
      return lastCartData || { total_price: 0, item_count: 0 };
    }
  }

  /**
   * Get free shipping limit from settings or data attribute
   */
  function getShippingLimit(container) {
    // Try to get from container data attribute first
    if (container && container.dataset.limit) {
      return parseInt(container.dataset.limit);
    }
    
    // Try to get from global settings
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.settings) {
      const limit = window.Shopify.theme.settings.free_shipping_limit;
      if (limit) {
        return parseInt(limit) * 100; // Convert dollars to cents
      }
    }
    
    // Try to get from theme settings variable
    if (typeof freeShippingLimit !== 'undefined') {
      return parseInt(freeShippingLimit) * 100;
    }
    
    // Fallback to default
    return 4900; // $49 in cents as absolute fallback
  }

  /**
   * Update a single shipping bar element (for cart and announcement)
   */
  function updateShippingBar(container, cartData) {
    if (!container) return;

    const limit = getShippingLimit(container);
    const subtotal = cartData.total_price || 0;
    const itemCount = cartData.item_count || 0;
    const location = container.dataset.location || 'cart';
    
    // Calculate progress
    const remaining = Math.max(0, limit - subtotal);
    const percent = Math.min(100, Math.ceil((subtotal / limit) * 100));
    const qualified = subtotal >= limit;

    // Get messages
    const msgDefault = container.dataset.msgDefault || 'Spend {{remaining}} more to get FREE SHIPPING!';
    const msgSuccess = container.dataset.msgSuccess || '🎉 You\'ve unlocked FREE SHIPPING!';
    const msgInitial = container.dataset.msgInitial || 'Free shipping on orders over $49!';

    // Determine visibility
    const shouldHide = itemCount === 0 && location !== 'announcement';

    if (shouldHide) {
      container.style.display = 'none';
      return;
    } else {
      container.style.display = '';
    }

    // Update qualified state
    container.dataset.qualified = qualified;

    // Update message
    const messageEl = container.querySelector(SELECTORS.message);
    if (messageEl) {
      let currentMessage;
      
      if (itemCount === 0) {
        currentMessage = msgInitial;
      } else if (qualified) {
        currentMessage = msgSuccess;
      } else {
        currentMessage = msgDefault.replace('{{remaining}}', formatMoney(remaining));
      }
      
      if (messageEl.textContent !== currentMessage) {
        messageEl.textContent = currentMessage;
        
        // Add subtle animation
        messageEl.style.opacity = '0';
        setTimeout(() => {
          messageEl.style.transition = 'opacity 0.3s ease';
          messageEl.style.opacity = '1';
        }, 50);
      }
    }

    // Update progress bar (not in announcement)
    if (location !== 'announcement') {
      const progressEl = container.querySelector(SELECTORS.progress);
      if (progressEl) {
        progressEl.style.width = `${percent}%`;
        
        // Apply color from data attribute if available
        const barColor = progressEl.dataset.barColor;
        if (barColor) {
          progressEl.style.backgroundColor = barColor;
        }
      }

      const percentageEl = container.querySelector(SELECTORS.percentage);
      if (percentageEl) {
        percentageEl.textContent = `${percent}%`;
      }

      // Update confetti
      const confettiEl = container.querySelector(SELECTORS.confetti);
      if (confettiEl) {
        if (qualified && itemCount > 0) {
          confettiEl.style.display = 'block';
          // Restart animation by removing and re-adding
          const pieces = confettiEl.querySelectorAll('.confetti-piece');
          pieces.forEach(piece => {
            piece.style.animation = 'none';
            setTimeout(() => {
              piece.style.animation = '';
            }, 10);
          });
        } else {
          confettiEl.style.display = 'none';
        }
      }
    }
  }

  /**
   * Update product page simple progress bar
   */
  function updateProductBar(container, cartData) {
    if (!container) return;

    const limit = getShippingLimit(container);
    const subtotal = cartData.total_price || 0;
    const itemCount = cartData.item_count || 0;
    
    // Calculate progress
    const remaining = Math.max(0, limit - subtotal);
    const percent = Math.min(100, Math.ceil((subtotal / limit) * 100));
    const qualified = subtotal >= limit;

    // Get messages
    const msgDefault = container.dataset.msgDefault || 'Spend {{remaining}} more to get FREE SHIPPING!';
    const msgSuccess = container.dataset.msgSuccess || '🎉 You\'ve unlocked FREE SHIPPING!';

    // Find initial message element
    const initialMessageEl = document.querySelector('[data-initial-message]');

    // Toggle between initial message and progress bar
    if (itemCount === 0) {
      // Show initial message, hide progress bar
      if (initialMessageEl) {
        initialMessageEl.style.display = '';
      }
      container.style.display = 'none';
      return;
    } else {
      // Hide initial message, show progress bar
      if (initialMessageEl) {
        initialMessageEl.style.display = 'none';
      }
      container.style.display = '';
    }

    // Update qualified state
    container.dataset.qualified = qualified;

    // Update progress bar
    const progressEl = container.querySelector(SELECTORS.productProgress);
    if (progressEl) {
      progressEl.style.width = `${percent}%`;
      
      // Apply color from settings or data attribute
      const barColor = progressEl.dataset.barColor || 
                      window.freeShippingBarColor || 
                      '#449424';
      progressEl.style.backgroundColor = barColor;
    }

    // Update message
    const messageEl = container.querySelector(SELECTORS.productMessage);
    if (messageEl) {
      let currentMessage;
      
      if (qualified) {
        currentMessage = `<span class="free-shipping-simple__success">${msgSuccess}</span>`;
      } else {
        currentMessage = msgDefault.replace('{{remaining}}', formatMoney(remaining));
      }
      
      if (messageEl.innerHTML !== currentMessage) {
        messageEl.innerHTML = currentMessage;
        
        // Add subtle animation
        messageEl.style.opacity = '0';
        setTimeout(() => {
          messageEl.style.transition = 'opacity 0.3s ease';
          messageEl.style.opacity = '1';
        }, 50);
      }
    }
  }

  /**
   * Update all shipping bars and product bars
   */
  async function updateAllBars() {
    if (updateInProgress) {
      updateQueued = true;
      return;
    }

    updateInProgress = true;

    // Add loading state
    const containers = document.querySelectorAll(SELECTORS.container);
    const productContainers = document.querySelectorAll(SELECTORS.productContainer);
    
    containers.forEach(c => c.classList.add('loading'));
    productContainers.forEach(c => c.classList.add('loading'));

    try {
      const cartData = await fetchCart();
      
      if (!cartData) {
        console.warn('Free Shipping Bar - No cart data received');
        return;
      }

      // Update cart/announcement bars
      containers.forEach(container => {
        updateShippingBar(container, cartData);
      });

      // Update product page simple progress bars
      productContainers.forEach(container => {
        updateProductBar(container, cartData);
      });

    } catch (error) {
      console.error('Free Shipping Bar - Update error:', error);
    } finally {
      // Remove loading state
      containers.forEach(c => c.classList.remove('loading'));
      productContainers.forEach(c => c.classList.remove('loading'));
      
      updateInProgress = false;

      // Process queued update
      if (updateQueued) {
        updateQueued = false;
        setTimeout(updateAllBars, 200);
      }
    }
  }

  /**
   * Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    // Listen to custom cart events
    const debouncedUpdate = debounce(updateAllBars, 300);
    
    CART_EVENTS.forEach(eventName => {
      document.addEventListener(eventName, debouncedUpdate);
      window.addEventListener(eventName, debouncedUpdate);
    });

    // Watch for form submissions (add to cart)
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || !form.action) return;

      if (form.action.includes('/cart/add') || 
          (form.id && form.id.includes('product-form')) ||
          form.classList.contains('product-form')) {
        setTimeout(() => {
          updateAllBars();
        }, 800);
      }
    });

    // Watch for quantity changes - plus/minus buttons
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Plus/minus buttons
      if (target.matches('[name="plus"], [name="minus"]') ||
          target.closest('[name="plus"], [name="minus"]') ||
          target.matches('.quantity__button') ||
          target.closest('.quantity__button')) {
        setTimeout(updateAllBars, 500);
      }

      // Remove buttons
      if (target.matches('.cart-remove-button, .cart-remove-button *') ||
          target.closest('.cart-remove-button') ||
          target.matches('[data-index]') ||
          target.closest('[data-index]')) {
        setTimeout(updateAllBars, 500);
      }
    });

    // Watch for direct input changes
    document.addEventListener('change', (e) => {
      const target = e.target;
      
      if (target.matches('input[name="updates[]"], input.quantity__input, input[type="number"]') &&
          target.closest('.cart-item, cart-items, .cart')) {
        setTimeout(updateAllBars, 500);
      }
    });

    // Watch for drawer opening (cart drawer)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList && 
              (target.classList.contains('active') || 
               target.classList.contains('is-open') ||
               target.classList.contains('opened'))) {
            updateAllBars();
          }
        }
      });
    });

    // Observe cart drawer
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, #cart-drawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Listen for Shopify theme events
    if (window.Shopify && window.Shopify.theme) {
      document.addEventListener('shopify:section:load', () => {
        setTimeout(updateAllBars, 300);
      });
    }
  }

  /**
   * Initialize on page load
   */
  function init() {
    // Check if free shipping is enabled
    const hasShippingBars = document.querySelector(SELECTORS.container) || 
                           document.querySelector(SELECTORS.productContainer);
    
    if (!hasShippingBars) {
      console.log('Free Shipping Bar - No bars found on page');
      return;
    }

    console.log('Free Shipping Bar - Initializing...');

    // Initial update
    updateAllBars();

    // Setup event listeners
    initEventListeners();

    console.log('Free Shipping Bar - Initialized successfully');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  // Also try after window load for safety
  window.addEventListener('load', () => {
    setTimeout(init, 100);
  });

  // Expose update function globally for manual triggers
  window.FreeShippingBar = {
    update: updateAllBars,
    init: init
  };

})();